/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { LoadingIndicator } from '@/components/AZCustomComponent/LoadingIndicator';
import type { AddToCartData } from '@/types';
import { eventConstants } from '@/constants/event';
import { NavBar } from '@/components/NavBar';
import { trackAddToCartCertonaCartridge } from '@/utils/analytics/track/trackAddToCartCertonaCartridge';
import usePrevious from '@/utils/usePrevious';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import type { SkuPricingAndAvailabilityFromAPI, AZBadgesFlagVOFromAPI } from '@/types/availability';
import type { LineItemFromState, OrdersDetailsFromState } from '@/types/reduxStore/orders';
import PriceComponent from '@/components/AZCustomComponent/Price';
import Image from '@/components/NextImage';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { imageConstant } from '@/constants/images';
import { countryCodes } from '@/constants/locale';
import { MiniCartFlyoutCheckout } from './MiniCartFlyoutCheckout';
import { CertonaBlock } from '@/features/certona';
import { useGetCertonaRecommendations } from '@/features/certona/api/useGetCertonaRecommendations';
import { certonaPageType } from '@/constants/certonaPageType';
import { useFeatureFlag } from '@/features/globalConfig';
import { type CartSummary } from '@/features/header';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { Label } from '@/features/i18n';
import { useLabels } from '@/hooks/useLabels';
import { useCountry, useLocale } from '@/hooks/useLocale';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { AddToCartResponse, CartSuccess } from '@/types/reduxStore/cartUpdate';
import azCommonStyles from '@/theme/globals.module.scss';
import { useDeviceType } from '@/utils/useDeviceType';
import cx from 'classnames';
import { useCallback, useEffect, useMemo, useState, type RefObject } from 'react';
import styles from './styles.module.scss';
import MiniCartFlyoutItemCard from './MiniCartFlyoutItemCard';
import MiniCartFlyoutDealImage from './MiniCartFlyoutDealImage';
import { StackedPartCardSkeleton } from '@/features/certona';
import { formatPrice } from '@/utils/validator/formatPrice';
import type { PageName } from '@/types/analytics';
import { sessionStorage } from '@/utils/sessionStorage';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { useOMSFlags } from '@/features/product/utils/getOMSFlags';
import type { CertonaRecommendation } from '@/features/certona';
import type { CertonaItem } from '@/types';
import { requestBaseURL } from '@/config/serviceAPI';
// eslint-disable-next-line no-restricted-imports
import type {
  AffinityRelatedProductsData,
  AffinityRelatedProductsOptionsACES,
} from '@/features/pdpAffinity/interface';
import type { CtjAnalyticsType } from '@/features/certona';
import { usePageType } from '@/hooks/usePageType';
import { useGetProductSkuPrice } from '@/features/product/api/getProductSkuPrice';
import { defaultStoreUS } from '@/constants/locale';
import { getAvailableCertonaProducts } from '@/utils/getAvailableCertonaProducts';
import { isPdpPage } from '@/utils/analytics/isPdpPage';
import {
  useAffinityRelatedProductsACES,
  getAffinityForCart,
} from '@/features/pdpAffinity/api/getAffinityRelatedProductsACES';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import { MINI_CART_TITLE_ID } from '../constants';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { LittlePromo } from './LittlePromo';
import { useProductAvailability } from '@/features/product/api/getProductAvailability';
import { LITTLE_PROMO_CONFIG } from '../config/littlePromoConfig';

const certonaMinicartObj = {
  '@type': 'MiniCart_Certona',
  containerID: 'addtocart',
  desktop: true,
  mobile: true,
  name: 'Certona Content Block',
  tablet: true,
  themeCode: 'white',
};

const certonaAcesDealsMinicartObj = {
  '@type': 'MiniCart_Certona',
  containerID: 'addtocart1_rr',
  desktop: true,
  mobile: true,
  name: 'Certona Content Block',
  tablet: true,
  themeCode: 'white',
};

type ProductDetails = {
  productName?: {
    productBrand: string | undefined;
    productPart: string | undefined;
    quickNote: string | undefined;
  };
  originalPartTypeId?: string | undefined;
  productPartType?: string | undefined;
  partTerminologyId?: string | undefined;
  productId?: string | undefined;
  productAvailabilityInfo?: {
    skuPricingAndAvailability: SkuPricingAndAvailabilityFromAPI | undefined;
    dealsInfo: unknown;
    aZBadgesFlagVO: AZBadgesFlagVOFromAPI | undefined | null;
  };
  certonaItemAddedTocart?: boolean;
  vehicleFitFlag?: boolean;
};

type Props = {
  handleClose: () => void;
  isModalOpen: boolean;
  closeStoreModal: () => void;
  successData:
    | (CartSuccess & { originalPartTypeId?: string; partTerminologyId?: string })
    | (AddToCartResponse & { quantity: string; partTerminologyId?: string })
    | null
    | undefined;
  miniCartData: CartSummary | undefined;
  image: string;
  productDetails: ProductDetails;
  cartUrl: () => void;
  productTitle: string;
  pageName: PageName;
  orderDetailsFromState: OrdersDetailsFromState;
  miniCartDealData?: Set<string>;
  skuId?: string | undefined;
  parentRef: RefObject<HTMLDivElement | null>;
  partGroupId?: string | null;
  pageLevelCertonaRecommendations?: CertonaRecommendation[];
  hideCompleteTheJob?: boolean;
};

const labelMap: Record<string, string> = {
  lblMiniCartAddedToCart: 'label_mini_cart_added_to_Cart',
  lblMiniCartQty: 'label_mini_cart_qty',
  lblMiniCartCorePrice: 'label_mini_cart_core_price',
  lblCart: 'label_cart_topNavigation_Cart',
  lblMiniCartSubTotal: 'label_mini_cart_sub_total',
  lblMiniCartContinueShop: 'label_mini_cart_continue_shopping',
  lblMiniCartItem: 'label_mini_cart_item',
  lblMiniCartItems: 'label_mini_cart_items',
  label_miniCart_affinityItems_title: 'label_miniCart_affinityItems_title',
};

export function MiniCartFlyoutComp(props: Props) {
  const useGCPRelatedPartsService = useMonetateDecisionFlag('useGCPRelatedPartsService');
  const { useOMSInventoryQohService } = useOMSFlags();
  const { data: storeDetails } = useStoreDetailsData();
  const {
    data: headerData,
    isFetching: isHeaderDataFetching,
    isSuccess: isHeaderDataSuccess,
  } = useHeaderData();
  const [itemAddedToCart, setItemAddedToCart] = useState(props.successData);
  const [hasLoadedCertona, setHasLoadedCertona] = useState(false);
  const [certonaRecommendationAddedToCart, setCertonaRecommendationAddedToCart] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const deviceType = useDeviceType();
  const isABot = deviceType === 'bot';
  const { data: shelfData } = useProductList();
  const parts = shelfData?.shelfParts ?? [];
  const locale = useLocale();
  const country = useCountry();
  const { storeNumber: storeId } = headerData || {};
  const preferredVehicle = headerData?.vehicleMap;
  const {
    image,
    productTitle,
    productDetails,
    miniCartData,
    isModalOpen,
    handleClose,
    miniCartDealData,
    skuId: skuIdFromProductDetail,
    pageLevelCertonaRecommendations,
    orderDetailsFromState,
    hideCompleteTheJob = false,
  } = props;
  const { vehicleFitFlag = true } = productDetails;
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const isDealsDrawerMiniCartEnabled =
    useFeatureFlag('IS_DEALS_DRAWER_MINI_CART_ENABLED') === 'true';
  const desktopMiniCartFlyoutEnabled =
    useFeatureFlag('IS_DESKTOP_MINI_CART_FLYOUT_ENABLED') === 'true';
  const mobileMiniCartFlyoutEnabled =
    useFeatureFlag('IS_MOBILE_MINI_CART_FLYOUT_ENABLED') === 'true';
  const tabletMiniCartFlyoutEnabled =
    useFeatureFlag('IS_TABLET_MINI_CART_FLYOUT_ENABLED') === 'true';
  const isElasticEnabled = useFeatureFlag('IS_AFFINITY_ELASTIC_ENABLED') === 'true';
  const isCompleteTheJobInMiniCartEnabled = useForteDecisionFlagWithTrueDefault(
    'enableCompleteTheJobInMiniCart',
  );
  const isGlovesOn75OrGreaterEnabled = useForteDecisionFlagWithTrueDefault('glovesOn75OrGreater');
  const isCertonaAddToCartEnabledFlag = useCertonaAddToCartFlag();
  const { pageType } = usePageType();

  const { data: glovesAvailability } = useProductAvailability({
    skuIds: [LITTLE_PROMO_CONFIG.SKU_ID],
    enabled: isGlovesOn75OrGreaterEnabled && LITTLE_PROMO_CONFIG.SUPPORTED_LOCALES.includes(locale),
  });
  const isMiniCartFlyoutEnabled =
    (isDesktop && desktopMiniCartFlyoutEnabled) ||
    (isMobile && mobileMiniCartFlyoutEnabled) ||
    (isTablet && tabletMiniCartFlyoutEnabled);
  const certonaAddToCartEnabled = !isABot && isCertonaAddToCartEnabledFlag;
  const isUseDealsCart = isDealsDrawerMiniCartEnabled && miniCartDealData !== undefined;

  const skuIds = skuIdFromProductDetail ? [skuIdFromProductDetail] : null;

  const { data: productSkuDetailsData } = useProductSkuDetails({
    skuIds,
  });

  useEffect(() => {
    setIsAffinityRelatedProductsError(false);
  }, [props]);

  const [affinityRelatedProductsData, setAffinityRelatedProductsData] =
    useState<AffinityRelatedProductsData>();

  const [isAffinityRelatedProductsSuccess, setIsAffinityRelatedProductsSuccess] = useState(false);
  const [isAffinityRelatedProductsPending, setIsAffinityRelatedProductsPending] = useState(false);
  const [isAffinityRelatedProductsError, setIsAffinityRelatedProductsError] = useState(false);
  const [recommendingAffinityItems, setRecommendingAffinityItems] = useState(false);
  const { data: affinityRelatedProductsForPDP } = useAffinityRelatedProductsACES({
    enabled: false,
  });

  useEffect(() => {
    window.certonaNotCalled = true;
  }, []);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode === eventConstants.escKeyCode) {
        handleClose();
      }
    };

    document.addEventListener('keyup', handleKeyDown);
    return () => {
      document.removeEventListener('keyup', handleKeyDown);
    };
  }, [isModalOpen, handleClose]);

  useEffect(() => {
    return () => {
      if (sessionStorage.getItem('skuIdSetStore')) {
        sessionStorage.removeItem('skuIdSetStore');
      }
    };
  }, []);

  const labels = useLabels(labelMap);

  const skuId =
    ((!miniCartDealData?.size &&
      itemAddedToCart &&
      'skuId' in itemAddedToCart &&
      itemAddedToCart.skuId) ||
      productDetails.productAvailabilityInfo?.skuPricingAndAvailability?.skuId ||
      skuIdFromProductDetail) ??
    '';

  const prevSkuId = usePrevious(skuId);
  useEffect(() => {
    if (prevSkuId !== undefined && prevSkuId !== skuId) {
      setIsAffinityRelatedProductsSuccess(false);
      setAffinityRelatedProductsData(undefined);
      setIsAffinityRelatedProductsPending(false);
      setIsAffinityRelatedProductsError(false);
    }
  }, [skuId, prevSkuId]);

  const addToCartItem =
    isABot && parts ? parts[0] : parts.find((item) => String(item.skuNumber) === skuId);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const addedProductImage =
    // this should be done by refactoring getProductList into separate hooks that are used by components directly
    // SEB-7290
    addToCartItem?.itemImageUrl ??
    (itemAddedToCart && 'imageUrl' in itemAddedToCart ? itemAddedToCart.imageUrl : undefined) ??
    addToCartItem?.imageUrl ??
    addToCartItem?.productImageUrl ??
    image ??
    '';
  const addedProductTitle =
    addToCartItem?.name ||
    (itemAddedToCart && 'label' in itemAddedToCart ? itemAddedToCart.label : undefined) ||
    productTitle ||
    '';
  const addedProductDetails = {
    productAvailabilityInfo: {
      skuPricingAndAvailability: skuIdFromProductDetail
        ? productSkuDetailsData?.[0].skuPricingAndAvailability
        : addToCartItem?.skuPricingAndAvailability ||
          productDetails.productAvailabilityInfo?.skuPricingAndAvailability,
    },
  };

  const findMiniCartItemBySkuId = (skuId: string) => {
    const orders = props.orderDetailsFromState.orderItems;
    if (!orders) {
      return null;
    }

    let foundLineItem: LineItemFromState | undefined;
    orders.find((order) => {
      order.shipmentInfoList.find((shipment) => {
        shipment.lineItemList.find((lineItem) => {
          if (lineItem.productInfo.skuId === skuId) {
            foundLineItem = lineItem;
            return true;
          }
        });

        if (foundLineItem) {
          return true;
        }
      });

      if (foundLineItem) {
        return true;
      }
    });
    return foundLineItem;
  };

  const findMiniCartDataBySku = (skuId: string): { itemImage: string; quantity: string } => {
    const cartOrders = props.orderDetailsFromState.orderItems;
    const dealData: { itemImage: string; quantity: string } = { itemImage: '', quantity: '0' };

    cartOrders.find((order) => {
      order.shipmentInfoList.find((shipment) => {
        shipment.lineItemList.find((lineItem) => {
          if (lineItem.productInfo.skuId === skuId) {
            dealData.itemImage = lineItem.productInfo.imageUrl;
            dealData.quantity = lineItem.itemQuantity;
            return true;
          }
        });
        if (dealData.quantity !== '0') {
          return true;
        }
      });
      if (dealData.quantity !== '0') {
        return true;
      }
    });
    return dealData;
  };
  const isDeal =
    miniCartDealData && isDealsDrawerMiniCartEnabled && miniCartDealData.size > 1 && !skuId;

  const getMiniCartItem = () => {
    if (!isDeal && miniCartDealData?.size === 1) {
      const [dealItemSku] = miniCartDealData;
      return findMiniCartItemBySkuId(dealItemSku);
    } else {
      const skuNumber = sessionStorage.getItem('skuIdSetStore') ?? skuId;
      return findMiniCartItemBySkuId(skuNumber ?? '');
    }
  };
  const miniCartItem = getMiniCartItem();
  const total = Number(miniCartItem?.itemQuantity ?? itemAddedToCart?.quantity ?? 1);

  const certonaOptions =
    isUseDealsCart && !skuId
      ? {
          certonaType: certonaPageType.dealsDrawer,
          skuIds: [...miniCartDealData].join(';'),
          vehicleID: preferredVehicle?.partsProVehicleId?.toString(),
        }
      : {
          certonaType: certonaPageType.cartAdd,
          skuIds: skuId,
          vehicleID: preferredVehicle?.partsProVehicleId?.toString(),
          isRecItemAddedToCart:
            (certonaRecommendationAddedToCart && certonaAddToCartEnabled) ||
            productDetails.certonaItemAddedTocart,
          parttype: certonaRecommendationAddedToCart
            ? itemAddedToCart?.partTerminologyId
            : productDetails.partTerminologyId?.toString(),
        };
  const dependencyArray = isUseDealsCart
    ? [preferredVehicle?.partsProVehicleId, isUseDealsCart, skuId, storeId]
    : [skuId, preferredVehicle?.partsProVehicleId, isMiniCartFlyoutEnabled, storeId];
  const shouldExecuteCertonaCall = isUseDealsCart
    ? isDealsDrawerMiniCartEnabled
    : isMiniCartFlyoutEnabled;

  // Determine if we should fetch cart-specific certona
  const shouldFetchCartCertona = useMemo(() => {
    // Have page certona AND it's first add = use page certona (no fetch needed)
    if (
      pageLevelCertonaRecommendations &&
      pageLevelCertonaRecommendations.length > 0 &&
      !certonaRecommendationAddedToCart
    ) {
      return false;
    }

    // Certona item added to cart = fetch cart certona for chaining
    // OR no page certona provided = fetch as before (backward compatibility)
    return true;
  }, [pageLevelCertonaRecommendations, certonaRecommendationAddedToCart]);

  // Conditional fetch (only when needed)
  const cartCertonaRecommendations = useGetCertonaRecommendations(
    certonaOptions,
    dependencyArray,
    shouldExecuteCertonaCall && shouldFetchCartCertona && isModalOpen,
    false,
  );

  // Products already in the cart shouldn't be recommended again. Certona returns one
  // recommendation block per container/scheme in a single response, so only filter
  // items belonging to the addtocart1_rr (minicart) scheme.
  const cartSkuIds = useMemo(() => {
    const skuIds = new Set<string>();
    orderDetailsFromState.orderItems?.forEach((order) => {
      order.shipmentInfoList.forEach((shipment) => {
        shipment.lineItemList.forEach((lineItem) => {
          skuIds.add(lineItem.productInfo.skuId);
        });
      });
    });
    return skuIds;
  }, [orderDetailsFromState]);

  // Cart certona can come back with every addtocart1_rr item already filtered out once
  // cart SKUs are excluded (e.g. the shopper already added every recommended item). In
  // that case there's nothing to prefer it over page certona for.
  const hasDisplayableCartCertona = useMemo(() => {
    return cartCertonaRecommendations.some((recommendation) =>
      recommendation.scheme === 'addtocart1_rr'
        ? recommendation.items.some((item) => !cartSkuIds.has(item.id))
        : recommendation.items.length > 0,
    );
  }, [cartCertonaRecommendations, cartSkuIds]);

  // Select which certona to use. Prefer cart certona once it has actually loaded and still
  // has displayable items after cart-SKU filtering, but while a fresh chained fetch
  // (triggered by adding a rec from inside the minicart) is still in flight, keep showing
  // page certona instead of flashing to an empty list.
  const certonaRecommendations = useMemo(() => {
    if (cartCertonaRecommendations.length > 0 && hasDisplayableCartCertona) {
      return cartCertonaRecommendations;
    }
    return pageLevelCertonaRecommendations && pageLevelCertonaRecommendations.length > 0
      ? pageLevelCertonaRecommendations
      : cartCertonaRecommendations;
  }, [cartCertonaRecommendations, pageLevelCertonaRecommendations, hasDisplayableCartCertona]);

  const certonaRecommendationsForDisplay = useMemo(() => {
    if (!cartSkuIds.size || !certonaRecommendations) {
      return certonaRecommendations;
    }

    return certonaRecommendations.map((recommendation) =>
      recommendation.scheme === 'addtocart1_rr'
        ? {
            ...recommendation,
            items: recommendation.items.filter((item) => !cartSkuIds.has(item.id)),
          }
        : recommendation,
    );
  }, [certonaRecommendations, cartSkuIds]);

  const prevCertonaRecommendations = usePrevious(certonaRecommendations);

  useEffect(() => {
    if (!!certonaRecommendations && certonaRecommendations !== prevCertonaRecommendations) {
      setHasLoadedCertona(true);
    }
  }, [certonaRecommendations, prevCertonaRecommendations]);

  const renderEachItemPrice = useCallback(() => {
    const amt = miniCartItem?.lineItemPriceInfo.unitPrice;

    if (total < 2) {
      return;
    }

    const isBopusEnabled = storeDetails?.bopusEnabled ?? false;
    const isBopusMexicoStoreEnabled = locale === countryCodes.mx && isBopusEnabled;
    const eachLabel = isBopusMexicoStoreEnabled ? (
      <Label label="label_cart_Each" />
    ) : (
      <Label label="label_cart_lineItem_Each" />
    );

    if (total > 1 && amt) {
      return (
        <div
          className={cx(
            azCommonStyles['az-body-3-regular'],
            azCommonStyles['az-margin-top-4xs'],
            azCommonStyles['az-align-center'],
            styles.unitPrice,
          )}
        >
          ${formatPrice(amt)} {eachLabel}
        </div>
      );
    }
  }, [locale, miniCartItem?.lineItemPriceInfo.unitPrice, storeDetails?.bopusEnabled, total]);

  const splitNumber = (price: number) => {
    return String(price.toFixed(2)).split('.');
  };

  const renderPricePart = useCallback(() => {
    const retailPrice = miniCartItem?.lineItemPriceInfo.retailPrice ?? 0;
    const dealAppliedAmount = miniCartItem?.lineItemPriceInfo.dealAppliedAmount ?? 0;

    const isDiscounted = miniCartItem?.lineItemPriceInfo.discounted ?? false;
    const discountedPrice = retailPrice && dealAppliedAmount ? retailPrice - dealAppliedAmount : 0;
    const corePriceValue = skuIdFromProductDetail
      ? (productSkuDetailsData?.[0].skuPricingAndAvailability.corePrice ?? 0)
      : (addedProductDetails.productAvailabilityInfo.skuPricingAndAvailability?.corePrice ??
        miniCartItem?.lineItemPriceInfo.corePrice ??
        0);

    let finalPrice: number;
    let originalPrice: number | null = null;
    let showDiscount = false;

    if (isDiscounted && discountedPrice) {
      finalPrice = discountedPrice;
      originalPrice = retailPrice;
      showDiscount = true;
    } else {
      finalPrice = retailPrice;
    }

    if (!finalPrice) {
      return null;
    }

    return (
      <>
        <PriceComponent
          wrapperClassName={
            miniCartItem?.productInfo.clearanceItem ? styles.priceComponentWrapper : ''
          }
          priceType="secondary"
          wasDealPrice={showDiscount && originalPrice ? splitNumber(originalPrice) : undefined}
          dealPrice={splitNumber(finalPrice)}
          wasVariant="rightAlign"
          corePriceValue={corePriceValue}
          miniCartFly={true}
          enableStrikeThru={false}
        />
        {miniCartItem?.productInfo.clearanceItem && (
          <div className={styles.clearance}>
            <Label label="label_clearance_badge" />
          </div>
        )}
      </>
    );
  }, [
    addedProductDetails.productAvailabilityInfo.skuPricingAndAvailability?.corePrice,
    miniCartItem?.lineItemPriceInfo.corePrice,
    miniCartItem?.lineItemPriceInfo.dealAppliedAmount,
    miniCartItem?.lineItemPriceInfo.discounted,
    miniCartItem?.lineItemPriceInfo.retailPrice,
    miniCartItem?.productInfo.clearanceItem,
    productSkuDetailsData,
    skuIdFromProductDetail,
  ]);

  const renderCoreDeposit = useCallback(() => {
    const corePriceValue = skuIdFromProductDetail
      ? (productSkuDetailsData?.[0].skuPricingAndAvailability.corePrice ?? 0)
      : itemAddedToCart
        ? (miniCartItem?.lineItemPriceInfo.corePrice ?? 0)
        : (addedProductDetails.productAvailabilityInfo.skuPricingAndAvailability?.corePrice ?? 0);

    if (corePriceValue > 0) {
      return (
        <div
          className={`${cx(azCommonStyles['az-caption'], styles.coreDeposit)} ${
            locale === countryCodes.mx ? styles.coreDepositMx : ''
          }`}
        >
          +$
          {formatPrice(corePriceValue)}
          {` `} {labels.lblMiniCartCorePrice}
        </div>
      );
    }
  }, [
    addedProductDetails.productAvailabilityInfo.skuPricingAndAvailability?.corePrice,
    itemAddedToCart,
    labels.lblMiniCartCorePrice,
    locale,
    miniCartItem?.lineItemPriceInfo.corePrice,
    productSkuDetailsData,
    skuIdFromProductDetail,
  ]);

  const updateMiniCartItem = (item?: AddToCartData & { orderType?: string }) => {
    miniCartDealData?.clear();
    // @ts-expect-error fix type of itemAddedToCart
    setItemAddedToCart(item);
    setCertonaRecommendationAddedToCart(true);
    sessionStorage.removeItem('skuIdSetStore');
  };

  const handleGetAffinityRelatedProductsResults = useCallback(async () => {
    setIsAffinityRelatedProductsPending(true);

    const getAffinityRelatedProducts = async () => {
      if (!(pageType === 'ProductDetail')) {
        const vehicleQuestions =
          extractVehicleQuestions(preferredVehicle?.vehicleQuestionsIds, true) ?? '';
        const inventorySource: 'OMS' | 'CIS' = useOMSInventoryQohService ? 'OMS' : 'CIS';
        const affinityOptions: AffinityRelatedProductsOptionsACES = {
          partGroupId:
            miniCartItem?.productInfo?.partGroupId ??
            addToCartItem?.partGroupId ??
            props.partGroupId ??
            '',
          country,
          year: preferredVehicle?.year ? String(preferredVehicle.year) : undefined,
          makeId: preferredVehicle?.makeId?.toString(),
          modelId: preferredVehicle?.modelId?.toString(),
          storeId,
          vehicleTypeId: preferredVehicle?.vehicleTypeId?.toString(),
          vehicleQuestions: (vehicleQuestions as string) || undefined,
          includeReviewSummary: 'true',
          inventorySource: useGCPRelatedPartsService ? inventorySource : undefined,
          useElasticSearch: isElasticEnabled,
          recordsPerPage: 6,
        };
        return await getAffinityForCart(affinityOptions, useGCPRelatedPartsService);
      }
      return affinityRelatedProductsForPDP ?? null;
    };

    await getAffinityRelatedProducts()
      .then((response: AffinityRelatedProductsData) => {
        setAffinityRelatedProductsData(response);
        setIsAffinityRelatedProductsSuccess(true);
      })
      .catch(() => {
        setIsAffinityRelatedProductsSuccess(false);
        setIsAffinityRelatedProductsError(true);
      })
      .finally(() => {
        setIsAffinityRelatedProductsPending(false);
      });
  }, [
    pageType,
    affinityRelatedProductsForPDP,
    preferredVehicle?.vehicleQuestionsIds,
    preferredVehicle?.year,
    preferredVehicle?.makeId,
    preferredVehicle?.modelId,
    preferredVehicle?.vehicleTypeId,
    miniCartItem?.productInfo?.partGroupId,
    addToCartItem?.partGroupId,
    props.partGroupId,
    country,
    storeId,
    useGCPRelatedPartsService,
    useOMSInventoryQohService,
    isElasticEnabled,
  ]);

  useEffect(() => {
    if (
      isCompleteTheJobInMiniCartEnabled &&
      !hideCompleteTheJob &&
      !isHeaderDataFetching &&
      !isAffinityRelatedProductsSuccess &&
      !isAffinityRelatedProductsPending &&
      !isAffinityRelatedProductsError &&
      vehicleFitFlag
    ) {
      handleGetAffinityRelatedProductsResults().catch(() => {});
    }
  }, [
    addToCartItem,
    handleGetAffinityRelatedProductsResults,
    isAffinityRelatedProductsPending,
    isAffinityRelatedProductsSuccess,
    isCompleteTheJobInMiniCartEnabled,
    isHeaderDataFetching,
    isAffinityRelatedProductsError,
    vehicleFitFlag,
    hideCompleteTheJob,
  ]);

  const affinityItems = useMemo(() => {
    return Array<CertonaRecommendation>({
      display: 'yes',
      explanation: labels.label_miniCart_affinityItems_title,
      items:
        affinityRelatedProductsData?.map((affinityRelatedProduct) => {
          const {
            skuId: id,
            productImageUrl: product_image_url,
            productName: product_name,
            seoUrl: product_url_relative,
            avgRating: average_rating,
            totalReviewsCount: total_review_count,
            productId,
            partTerminologyId,
            vehicleFit,
            vehicleFitment,
            availableFulfillmentIds,
          } = affinityRelatedProduct;

          return {
            id,
            product_image_url,
            product_name,
            productId,
            partgroup_id: productId?.toString(),
            product_url: `${requestBaseURL}${product_url_relative}`,
            product_url_relative,
            ...(!!average_rating && { average_rating }),
            ...(!!total_review_count && { total_review_count }),
            ...(!!partTerminologyId && { partTerminologyId }),
            ...(!!vehicleFit && { vehicleFit }),
            ...(!!vehicleFitment && { vehicleFitment }),
            ...(!!availableFulfillmentIds && { availableFulfillmentIds }),
          } as CertonaItem;
        }) ?? new Array<CertonaItem>(),
      scheme: 'addtocart1_rr',
    });
  }, [affinityRelatedProductsData, labels.label_miniCart_affinityItems_title]);

  const skuNumbers = affinityItems[0].items?.map((part) => part.id);
  const storeNumber = headerData?.storeNumber ?? defaultStoreUS;
  const { data: pricingInfo } = useGetProductSkuPrice({
    skuNumbers,
    storeNumber,
    enabled: !!storeNumber && !!skuNumbers?.length,
    staleTime: 5 * 60000, // 5 minutes
  });

  const items = affinityItems[0].items;

  const availableProducts = useMemo(() => {
    if (!pricingInfo || !hasLoadedCertona || !certonaAddToCartEnabled) {
      return items || [];
    }
    return (
      getAvailableCertonaProducts({
        items,
        skuDetailsData: null,
        pricingInfo,
        filterByPriceOnly: true,
      }) ?? []
    );
  }, [items, pricingInfo, hasLoadedCertona, certonaAddToCartEnabled]);

  const affinityOrCertonaItems = useMemo(() => {
    // Don't show Complete the Job items when hideCompleteTheJob is true
    if (hideCompleteTheJob) {
      return undefined;
    }

    const affinityAndCertonaHaveBeenCalled =
      isHeaderDataSuccess &&
      isAffinityRelatedProductsSuccess &&
      (hasLoadedCertona || locale === countryCodes.mx);

    if (isCompleteTheJobInMiniCartEnabled && !isAffinityRelatedProductsError && vehicleFitFlag) {
      if (affinityAndCertonaHaveBeenCalled) {
        if (affinityItems[0]?.items.length > 0 && availableProducts.length > 0) {
          setRecommendingAffinityItems(true);
          return affinityItems;
        }
      }
    }
    return undefined;
  }, [
    hideCompleteTheJob,
    isHeaderDataSuccess,
    isAffinityRelatedProductsSuccess,
    hasLoadedCertona,
    isCompleteTheJobInMiniCartEnabled,
    isAffinityRelatedProductsError,
    vehicleFitFlag,
    affinityItems,
    availableProducts.length,
    locale,
  ]);

  const certonaToShow = useMemo(() => {
    if (locale === countryCodes.us) {
      return certonaRecommendationsForDisplay;
    }
    return undefined;
  }, [certonaRecommendationsForDisplay, locale]);

  const isCompleteTheJob = affinityItems === affinityOrCertonaItems;
  const showCtjAddToCartCTA = isCompleteTheJob || certonaAddToCartEnabled;

  const leadingItemFulfillmentId = useMemo(() => {
    const firstLineItem =
      orderDetailsFromState?.orderItems?.[0]?.shipmentInfoList?.[0]?.lineItemList?.[0];
    return firstLineItem?.selectedFulfillmentType ?? undefined;
  }, [orderDetailsFromState]);

  const hasGlovesInCart = useMemo(() => {
    if (!orderDetailsFromState?.orderItems) {
      return false;
    }
    return orderDetailsFromState.orderItems.some((order) =>
      order.shipmentInfoList?.some((shipment) =>
        shipment.lineItemList?.some(
          (lineItem) =>
            lineItem.productInfo?.skuId === LITTLE_PROMO_CONFIG.SKU_ID ||
            lineItem.productInfo?.productId === LITTLE_PROMO_CONFIG.PRODUCT_ID,
        ),
      ),
    );
  }, [orderDetailsFromState]);

  const hasSthAvailability = useMemo(() => {
    if (!glovesAvailability || glovesAvailability.length === 0) {
      return false;
    }
    const sthQuantity = glovesAvailability[0]?.availabilityInfoVO?.sthQuantity ?? 0;
    return sthQuantity > 0;
  }, [glovesAvailability]);

  const sthSubtotal = useMemo(() => {
    return (orderDetailsFromState.orderItems ?? []).reduce((total, order) => {
      return (
        total +
        order.shipmentInfoList.reduce((shipmentTotal, shipment) => {
          return shipment.shipmentMethod === 'hardgoodShippingGroup'
            ? shipmentTotal + shipment.shipmentPriceInfo.subTotal
            : shipmentTotal;
        }, 0)
      );
    }, 0);
  }, [orderDetailsFromState]);

  const shouldShowLittlePromo =
    isGlovesOn75OrGreaterEnabled &&
    LITTLE_PROMO_CONFIG.SUPPORTED_LOCALES.includes(locale) &&
    sthSubtotal >= LITTLE_PROMO_CONFIG.MINIMUM_STH_SUBTOTAL &&
    !hasGlovesInCart &&
    hasSthAvailability;

  const getCtjAnalytics = (skuId: string): CtjAnalyticsType | undefined => {
    if (!isCompleteTheJobInMiniCartEnabled || skuId === undefined) {
      return undefined;
    }
    const itemIndex = affinityItems?.[0].items.findIndex((eachItem) => eachItem.id === skuId);
    const ctjCustomLinkName = `miniCartAffinityCart Addition: ${itemIndex + 1} of ${
      affinityItems?.[0].items.length
    }`;

    const ctjAnalytics = {
      customLinkName: ctjCustomLinkName,
      productFindingMethod: 'Product Recommendation',
      productRecommendationInteraction: 'productRecommendationInteraction',
      pfmLocation: 'Affinity: Mini Cart',
      productRecommendationTypeList: ['Affinity'],
      productRecommendationType: 'Mini Cart',
      productBrand: affinityRelatedProductsData?.[itemIndex].brandName,
      ...(isPdpPage(pageType) && {
        enhancedPDPContent: sessionStorage.getItem('enhancedPDPContent') === 'true',
      }),
    };

    return ctjAnalytics;
  };

  return (
    <>
      <div className={styles.miniCartContainer}>
        <NavBar
          classes={{
            mainContainer: styles.navBarContainer,
            closeIconBtn: styles.navBarCloseIcon,
          }}
          showBackButton={false}
          showCloseButton
          handleClose={props.handleClose}
        />
        {isFetchingData ? (
          <div className={styles.loadingSpinner}>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            <div
              className={
                isDeal
                  ? cx(styles.productContainer, styles.productContainerBorderLine)
                  : styles.productContainer
              }
            >
              <div data-testid="minicart-flyout-added-to-cart" className={styles.displayFlex}>
                <div className={(cx(azCommonStyles['az-margin-top-s']), styles.checkMark)}>
                  <Image
                    src={imageConstant.greenTickRoundedMA.src}
                    width={20}
                    height={20}
                    className={styles.addVehicleWarning}
                    alt=""
                  />
                </div>
                <div>
                  <h2
                    id={MINI_CART_TITLE_ID}
                    className={cx(azCommonStyles.ml6, styles.addedToCart)}
                  >
                    {labels.lblMiniCartAddedToCart}
                  </h2>
                </div>
              </div>
              {isDeal ? (
                <div className={styles.dealImagesContainer}>
                  {Array.from(miniCartDealData).map((skuId) => {
                    const dealItem = findMiniCartDataBySku(skuId);
                    return (
                      <MiniCartFlyoutDealImage
                        key={skuId}
                        imageUrl={dealItem.itemImage}
                        quantity={dealItem.quantity}
                      />
                    );
                  })}
                </div>
              ) : (
                <MiniCartFlyoutItemCard
                  addedProductImage={miniCartItem?.productInfo?.imageUrl ?? addedProductImage}
                  addedProductTitle={miniCartItem?.productInfo.skuDisplayName ?? addedProductTitle}
                  quantityLabel={labels.lblMiniCartQty}
                  total={total}
                  renderCoreDeposit={renderCoreDeposit}
                  renderEachItemPrice={renderEachItemPrice}
                  renderPricePart={renderPricePart}
                />
              )}
            </div>
            {miniCartData && (
              <MiniCartFlyoutCheckout
                miniCartData={props.orderDetailsFromState.orderItems}
                miniCartMap={miniCartData}
                handleClose={props.handleClose}
              />
            )}
            {shouldShowLittlePromo && (
              <LittlePromo
                updateMiniCartItem={updateMiniCartItem}
                parentRef={props.parentRef}
                productAvailability={glovesAvailability}
              />
            )}
            {affinityOrCertonaItems && (
              <CertonaBlock
                LoadingSkeleton={StackedPartCardSkeleton}
                className={styles.certonaMiniCartFlyout}
                contentItem={
                  isUseDealsCart && !skuId ? certonaAcesDealsMinicartObj : certonaMinicartObj
                }
                certonaRecommendations={affinityOrCertonaItems}
                checkContainerNameIncludes
                containerType={'horizontal_stacked'}
                onCertonaAddToCartClick={(addToCartData) => {
                  miniCartDealData?.clear();
                  setIsFetchingData(true);
                  setCertonaRecommendationAddedToCart(true);
                  updateMiniCartItem({
                    ...addToCartData,
                    orderType:
                      addToCartData.fulFillmentId === FULFILLMENT_METHODS.ONLINEORDER
                        ? 'ONLINEORDER'
                        : 'STOREORDER',
                  });

                  setIsFetchingData(false);
                  trackAddToCartCertonaCartridge(addToCartData, '', isUseDealsCart, true, true);
                  setTimeout(() => {
                    props?.parentRef?.current?.scrollTo(0, 0);
                  }, 500);
                }}
                updateAfterAddtoCartClick={
                  !recommendingAffinityItems ? updateMiniCartItem : undefined
                }
                inDrawer
                onlyShowAvailableProductRecommendations={hasLoadedCertona}
                certonaType={
                  isCompleteTheJobInMiniCartEnabled && isCompleteTheJob ? 'CTJ' : undefined
                }
                getCtjAnalytics={getCtjAnalytics}
                showAddToCartCTA={showCtjAddToCartCTA}
                leadingItemFulfillmentId={leadingItemFulfillmentId}
              />
            )}
            {certonaToShow?.some((recommendation) => recommendation.items.length > 0) ? (
              <CertonaBlock
                LoadingSkeleton={StackedPartCardSkeleton}
                className={styles.certonaMiniCartFlyout}
                contentItem={
                  isUseDealsCart && !skuId ? certonaAcesDealsMinicartObj : certonaMinicartObj
                }
                certonaRecommendations={certonaToShow}
                checkContainerNameIncludes
                containerType={'horizontal_stacked'}
                onCertonaAddToCartClick={(addToCartData) => {
                  miniCartDealData?.clear();
                  setIsFetchingData(true);
                  setCertonaRecommendationAddedToCart(true);
                  updateMiniCartItem({
                    ...addToCartData,
                    orderType:
                      addToCartData.fulFillmentId === FULFILLMENT_METHODS.ONLINEORDER
                        ? 'ONLINEORDER'
                        : 'STOREORDER',
                  });

                  setIsFetchingData(false);
                  trackAddToCartCertonaCartridge(addToCartData, '', isUseDealsCart, true, true);
                  setTimeout(() => {
                    props?.parentRef?.current?.scrollTo(0, 0);
                  }, 500);
                }}
                updateAfterAddtoCartClick={updateMiniCartItem}
                inDrawer
                onlyShowAvailableProductRecommendations={hasLoadedCertona}
                showAddToCartCTA={certonaAddToCartEnabled}
                leadingItemFulfillmentId={leadingItemFulfillmentId}
              />
            ) : null}
          </>
        )}
      </div>
    </>
  );
}
